<template>
  <div class="gs-perf">
    <div class="gs-perf__scroll">
      <table class="gs-perf__table">
        <thead>
          <tr>
            <th
              v-for="column in columns"
              :key="column.key"
              :data-column="column.key"
              :class="['gs-perf__head', { 'gs-perf__head--numeric': column.numeric }]"
              scope="col"
              :aria-sort="sortStateFor(column.key)"
            >
              <button type="button" class="gs-perf__sort" @click="$emit('sort', column.key)">
                <span>{{ column.label }}</span>
                <gs-icon
                  v-if="orderBy === column.key"
                  :name="ascending ? 'chevron-up' : 'chevron-down'"
                  size="14"
                  class="gs-perf__sort-icon"
                />
              </button>
            </th>
          </tr>
        </thead>

        <tbody>
          <tr v-if="loading" class="gs-perf__state-row">
            <td :colspan="columns.length" class="gs-perf__state">Loading video performance…</td>
          </tr>
          <tr v-else-if="!rows.length" class="gs-perf__state-row">
            <td :colspan="columns.length" class="gs-perf__state">No videos in this range</td>
          </tr>
          <template v-else>
          <tr
            v-for="row in rows"
            :key="row.id"
            :data-id="row.id"
            class="gs-perf__row"
            tabindex="0"
            @click="$emit('open', row.id)"
            @keydown.enter="$emit('open', row.id)"
          >
            <td data-cell="name" class="gs-perf__cell gs-perf__cell--name">
              <span class="gs-perf__name">{{ row.name }}</span>
              <span class="gs-perf__created">{{ row.created_at }}</span>
            </td>
            <td data-cell="impressions" class="gs-perf__cell gs-perf__cell--numeric">
              {{ number(row.impressions) }}
            </td>
            <td data-cell="uniques" class="gs-perf__cell gs-perf__cell--numeric">
              {{ number(row.uniques) }}
            </td>
            <td data-cell="plays" class="gs-perf__cell gs-perf__cell--numeric">
              {{ percent ? percentage(row.plays_percentage) : number(row.plays) }}
            </td>
            <td data-cell="finishes" class="gs-perf__cell gs-perf__cell--numeric">
              {{ percent ? percentage(row.finishes_percentage) : number(row.finishes) }}
            </td>
            <td data-cell="total_time_watched" class="gs-perf__cell gs-perf__cell--numeric">
              {{ row.total_time_watched }}
            </td>
          </tr>
          </template>
        </tbody>
      </table>
    </div>

    <div class="gs-perf__footer">
      <label class="gs-perf__page-size">
        <span>Rows per page</span>
        <select
          data-action="limit"
          class="gs-perf__select"
          :value="limit"
          @change="$emit('update:limit', Number($event.target.value))"
        >
          <option v-for="size in pageSizes" :key="size" :value="size">{{ size }}</option>
        </select>
      </label>

      <p class="gs-perf__count">{{ countLabel }}</p>

      <div class="gs-perf__pager">
        <gs-button
          variant="outline"
          size="sm"
          data-action="prev"
          :disabled="page <= 1"
          @click="$emit('update:page', page - 1)"
        >
          Previous
        </gs-button>
        <gs-button
          variant="outline"
          size="sm"
          data-action="next"
          :disabled="page >= pageCount"
          @click="$emit('update:page', page + 1)"
        >
          Next
        </gs-button>
      </div>
    </div>
  </div>
</template>

<script>
export const PAGE_SIZES = [10, 25, 50];

export default {
  name: 'VideoPerformanceTable',
  props: {
    rows: { type: Array, default: () => [] },
    total: { type: Number, default: 0 },
    page: { type: Number, default: 1 },
    limit: { type: Number, default: 10 },
    orderBy: { type: String, default: 'id' },
    ascending: { type: Boolean, default: false },
    percent: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
  },
  data() {
    return { pageSizes: PAGE_SIZES };
  },
  computed: {
    columns() {
      return [
        { key: 'name', label: 'Video', numeric: false },
        { key: 'impressions', label: 'Player loads', numeric: true },
        { key: 'uniques', label: 'Daily unique viewers', numeric: true },
        { key: 'plays', label: 'Plays', numeric: true },
        { key: 'finishes', label: 'Finishes', numeric: true },
        { key: 'total_time_watched', label: 'Watch time', numeric: true },
      ];
    },
    pageCount() {
      return Math.max(1, Math.ceil((this.total || 0) / (this.limit || 1)));
    },
    countLabel() {
      if (!this.total) return 'No videos';
      const first = (this.page - 1) * this.limit + 1;
      const last = Math.min(this.page * this.limit, this.total);
      return `${first}–${last} of ${this.total} videos`;
    },
  },
  methods: {
    sortStateFor(column) {
      if (this.orderBy !== column) return 'none';
      return this.ascending ? 'ascending' : 'descending';
    },
    number(value) {
      const n = Number(value);
      return Number.isFinite(n) ? n.toLocaleString('en-US') : '0';
    },
    percentage(value) {
      const n = Number(value);
      return `${Number.isFinite(n) ? n : 0}%`;
    },
  },
};
</script>

<style scoped>
.gs-perf {
  display: flex;
  flex-direction: column;
}

.gs-perf__scroll {
  overflow-x: auto;
}

.gs-perf__table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.gs-perf__head {
  padding: 0;
  color: var(--gs-muted-foreground);
  text-align: left;
  border-bottom: 1px solid var(--gs-border);
  white-space: nowrap;
}

.gs-perf__head--numeric {
  text-align: right;
}

.gs-perf__sort {
  display: inline-flex;
  gap: 6px;
  align-items: center;
  width: 100%;
  padding: 10px 14px;
  color: inherit;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: .04em;
  text-transform: uppercase;
  background: transparent;
  border: 0;
  cursor: pointer;
}

.gs-perf__head--numeric .gs-perf__sort {
  justify-content: flex-end;
}

.gs-perf__sort:hover {
  color: var(--gs-foreground);
}

.gs-perf__sort:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px var(--gs-ring);
}

.gs-perf__sort-icon {
  flex: none;
  color: var(--gs-primary);
}

.gs-perf__row {
  cursor: pointer;
}

.gs-perf__row:hover {
  background: var(--gs-surface-subtle);
}

.gs-perf__row:focus-visible {
  outline: none;
  background: var(--gs-active-soft);
}

.gs-perf__cell {
  padding: 12px 14px;
  color: var(--gs-foreground);
  border-bottom: 1px solid var(--gs-border);
  white-space: nowrap;
}

.gs-perf__cell--numeric {
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.gs-perf__cell--name {
  min-width: 220px;
  white-space: normal;
}

.gs-perf__name {
  display: block;
  font-weight: 600;
}

.gs-perf__created {
  display: block;
  margin-top: 2px;
  color: var(--gs-muted-foreground);
  font-size: 12px;
}

.gs-perf__state {
  padding: 32px 14px;
  color: var(--gs-muted-foreground);
  text-align: center;
}

.gs-perf__footer {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
  justify-content: space-between;
  padding: 14px 4px 0;
}

.gs-perf__page-size {
  display: inline-flex;
  gap: 8px;
  align-items: center;
  color: var(--gs-muted-foreground);
  font-size: 13px;
}

.gs-perf__select {
  height: 32px;
  padding: 0 8px;
  color: var(--gs-foreground);
  background: var(--gs-input);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-sm);
  font-size: 13px;
}

.gs-perf__select:focus {
  outline: none;
  box-shadow: 0 0 0 3px var(--gs-ring);
}

.gs-perf__count {
  margin: 0;
  color: var(--gs-muted-foreground);
  font-size: 13px;
  font-variant-numeric: tabular-nums;
}

.gs-perf__pager {
  display: inline-flex;
  gap: 8px;
}
</style>
